'use client';

import { useMemo, useState } from 'react';

import Faq from './faq.component';
import { HelpSearchHero } from './help-search-hero.component';
import { MostAccessedSection } from './most-accessed-section.component';
import { RescheduleCancelSection } from './reschedule-cancel-section.component';

import { HealthCareSection } from '@/app/(main)/(pages)/_components/health-care-section/health-care-section.component';
import { filterFaqContent } from '@/app/(main)/(pages)/(institucional)/preciso-de-ajuda/_utils/filter-faq-content.util';
import { ContentContainer } from '@/components/ui/content-container.component';
import { FaqTopic } from '@/app/(main)/(pages)/(institucional)/preciso-de-ajuda/_types/faq.type';
import { HelpPost } from '@/app/(main)/(pages)/(institucional)/preciso-de-ajuda/_types/help-post.type';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useDebounceValue } from '@/hooks/use-debounce.hook';
import { DownloadAppSection } from '@/app/(main)/(pages)/_components/download-app-section/download-app-section.component';

interface FaqPatientTabContentProps {
  faqContent: FaqTopic[];
  helpPosts: HelpPost[];
}

export const FaqPatientTabContent = ({ faqContent, helpPosts }: FaqPatientTabContentProps) => {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounceValue(query, 300);
  const isSearching = debouncedQuery.trim().length > 0;

  const { filtered, expandedTopics } = useMemo(
    () => filterFaqContent(faqContent, debouncedQuery),
    [faqContent, debouncedQuery],
  );

  return (
    <>
      <HelpSearchHero value={query} onChange={setQuery} />
      <ContentContainer className="lg:max-w-3xl">
        {!isSearching && <MostAccessedSection helpPosts={helpPosts} />}
        {!isSearching && <RescheduleCancelSection />}
        <section className="flex flex-col w-full pt-10">
          {isSearching && (
            <Typography
              variant={TypographyVariant.LARGE_SUBTITLE}
              as="h2"
              className="font-bold text-gray-900 mb-4"
            >
              Resultados para:
              {' '}
              {debouncedQuery}
            </Typography>
          )}
        </section>
      </ContentContainer>
      <ContentContainer className="sm:px-0 lg:max-w-3xl">
        {filtered.length > 0
          ? (
              <Faq
                key={`q:${debouncedQuery}`}
                content={filtered}
                defaultExpandedTopics={isSearching ? expandedTopics : undefined}
              />
            )
          : (
              <Typography variant={TypographyVariant.BODY_TEXT} as="p" className="text-gray-700">
                Nenhuma resposta encontrada.
              </Typography>
            )}
      </ContentContainer>
      <HealthCareSection isNeedHelpPage />
      <DownloadAppSection />
    </>
  );
};
